import React, { useRef, useEffect, useState } from "react";
import classnames from "classnames";

/** Fires once when the element scrolls into the middle 50% of the viewport. */
const useIntersectionTrigger = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTriggered(true);
          observer.disconnect();
        }
      },
      { rootMargin: "-25% 0px -25% 0px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, triggered };
};

const BOUNCE = "cubic-bezier(0.34, 1.15, 0.64, 1)";

export const BarChart = () => {
  const { ref, triggered } = useIntersectionTrigger();
  const data = [
    { category: "Street", lawsuits: 430 },
    { category: "ICE Check-ins", lawsuits: 301 },
    { category: "USCIS Appointments", lawsuits: 101 },
    { category: "Immigration Court Visits", lawsuits: 38 },
  ];

  return (
    <div className="my-6" ref={ref}>
      <h3 className="eyebrow mb-4 has-text-weight-medium">
        STREET ARRESTS WERE THE MOST COMMON TYPE OF ARREST IN THE PETITIONS
      </h3>
      <div className="eyebrow mb-2 is-small has-text-weight-medium">
        TOP 4 ARREST TYPES
      </div>
      <div className="bar-chart-content">
        {data.map((item, i) => (
          <div key={i}>
            <div
              key={item.category}
              className="is-flex is-flex-direction-row mb-4"
            >
              {/* Colored bar representing the number of lawsuits */}
              <div
                style={{
                  width: `calc(${(item.lawsuits / data[0].lawsuits) * 100}% - ${
                    (item.lawsuits / data[0].lawsuits) * 180
                  }px)`,
                  height: "60px",
                  backgroundColor: "#cd2927", // City Red
                  opacity: i === 0 ? 1 : 0.4,
                  transformOrigin: "left center",
                  transform: triggered ? "scaleX(1)" : "scaleX(0)",
                  transition: `transform 1600ms ${BOUNCE} ${i * 240}ms`,
                }}
              />
              <div
                className={classnames(
                  "eyebrow is-flex is-flex-direction-column is-justify-content-center ml-3",
                  i === 0 ? "has-text-weight-bold" : "has-text-weight-light",
                )}
                style={{
                  opacity: triggered ? 1 : 0,
                  transform: triggered ? "translateX(0)" : "translateX(-8px)",
                  transition: `opacity 800ms ease ${
                    i * 240 + 300
                  }ms, transform 800ms ${BOUNCE} ${i * 240 + 300}ms`,
                }}
              >
                <div>{item.lawsuits}</div>
                <div>{item.category}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const WaffleChart = () => {
  const { ref, triggered } = useIntersectionTrigger();
  const totalStreetArrests = 430;
  const latinoStreetArrests = 402;

  const percentageLatino = (latinoStreetArrests / totalStreetArrests) * 100;
  const filledSquares = Math.round(percentageLatino);

  return (
    <div className="my-6" ref={ref}>
      <h3 className="eyebrow mb-5 has-text-weight-medium">
        93% OF THESE STREET ARRESTS TARGETED LATINOS
      </h3>
      <div className="eyebrow mb-2 is-small has-text-weight-medium">
        1 SQUARE = 1% OF ARRESTS
      </div>
      <div className="is-flex is-flex-direction-row waffle-chart-content">
        <div style={{ position: "relative" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(10, 25px)",
              gridGap: "5px",
              padding: "5px",
              width: "max-content",
              transform: "scaleY(-1)", // Flip the grid on its head to fill from bottom to top
            }}
          >
            {Array.from({ length: 100 }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: "25px",
                  height: "25px",
                  backgroundColor: "#f4ce47", // City Yellow
                  // Start all at 0.4 opacity; on trigger, fade in the filled squares
                  opacity: triggered ? (i < filledSquares ? 1 : 0.2) : 0.2,
                  transition: triggered
                    ? `opacity 500ms ease ${i * 10}ms`
                    : "none",
                }}
              />
            ))}
          </div>
          {/* 6-edge dotted border — fades in after squares settle */}
          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
              overflow: "visible",
              opacity: triggered ? 1 : 0,
              transition: "opacity 600ms ease 1100ms",
            }}
            width={305}
            height={305}
            viewBox="0 0 305 305"
          >
            <polygon
              points="2.5,2.5 92.5,2.5 92.5,32.5 302.5,32.5 302.5,302.5 2.5,302.5"
              fill="none"
              stroke="white"
              strokeWidth={1}
              strokeDasharray="2 4"
            />
          </svg>
        </div>
        <div className="is-flex is-flex-direction-column is-justify-content-space-between ml-3 waffle-chart-labels">
          <div className="eyebrow has-text-weight-light">
            {totalStreetArrests} TOTAL
            <br />
            STREET ARRESTS
          </div>

          {/* Fades in after the outline appears */}
          <div
            className="eyebrow has-text-weight-bold"
            style={{
              opacity: triggered ? 1 : 0,
              transition: "opacity 600ms ease 1300ms",
            }}
          >
            {latinoStreetArrests} ARRESTS
            <br />
            OF LATINOS
          </div>
          {/* This is a placeholder to ensure the "{totalStreetArrests} TOTAL STREET ARRESTS" label is vertically centered with the grid. 
            It has the same height as the "{latinoStreetArrests} ARRESTS OF LATINOS" label but is invisible. */}
          <div
            className="eyebrow has-text-weight-light waffle-chart-label-placeholder"
            style={{ opacity: 0 }}
          >
            {totalStreetArrests} TOTAL
            <br />
            STREET ARRESTS
          </div>
        </div>
      </div>
    </div>
  );
};

export const ColumnChart = () => {
  const { ref, triggered } = useIntersectionTrigger();
  const data = [
    { category: "Street Arrests", percent: 0.93 },
    { category: "Arrests in Administrative Offices", percent: 0.55 },
    { category: "Immigrants in Region Without Legal Status", percent: 0.66 },
  ];
  return (
    <div className="my-6" ref={ref}>
      <h3 className="eyebrow mb-5 has-text-weight-medium">
        LATINOS WERE VASTLY OVERREPRESENTED IN STREET ARRESTS
      </h3>
      <div className="is-flex is-flex-direction-row is-justify-content-space-around column-chart-content">
        {data.map((item, i) => (
          <div
            className="is-flex is-flex-direction-column is-align-items-center"
            style={{ width: "33.3333%" }}
            key={i}
          >
            {/* Wrapper scaled from bottom — animates both bar segments together */}
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                transformOrigin: "bottom center",
                transform: triggered ? "scaleY(1)" : "scaleY(0)",
                transition: `transform 1500ms ${BOUNCE} ${i * 300}ms`,
              }}
            >
              <div
                className="data-column"
                style={{
                  width: "50%",
                  height: `${(1 - item.percent) * 300}px`,
                  backgroundColor: "#80b88d", // City Green
                  opacity: 0.4,
                }}
              />
              {/* Percent label — fades in after the bounce completes */}
              <div
                className={classnames("eyebrow", "has-text-weight-light")}
                style={{ position: "relative", width: "100%", height: "0px" }}
              >
                <div
                  className="column-chart-percent-label"
                  style={{
                    opacity: triggered ? 1 : 0,
                    transition: `opacity 800ms ease ${i * 300 + 800}ms`,
                  }}
                >
                  <span className="has-text-weight-bold">
                    {Math.round(item.percent * 100)}%
                  </span>{" "}
                  <br />
                  Latino
                </div>
              </div>
              <div
                className="data-column"
                style={{
                  width: "50%",
                  height: `${item.percent * 300}px`,
                  opacity: 1,
                  backgroundColor: "#80b88d",
                }}
              />
            </div>
            <div
              style={{ width: "100%", height: "1px", backgroundColor: "white" }}
            />
            <div className="eyebrow mt-2 has-text-weight-light has-text-centered px-3">
              {item.category}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
